import { IconGift } from '@tabler/icons-react';
import type { AuthPresentation } from '@/components/auth/auth-card';
import { VideoCreditTrialGuidance } from '@/components/shared/video-credit-trial-guidance';
import { websiteConfig } from '@/config/website';
import { getLocale } from '@/lib/locale';
import { cn } from '@/lib/utils';
import {
  formatLoginCreditUsage,
  type LoginCreditOfferContext,
  type LoginCreditOfferLocale,
} from './login-credit-offer';

interface LoginCreditOfferPanelProps {
  context?: LoginCreditOfferContext;
  presentation?: AuthPresentation;
}

const FREE_CREDIT_LABELS: Record<LoginCreditOfferLocale, string> = {
  en: 'Free Credits',
  es: 'créditos gratis',
};

export function LoginCreditOfferPanel({
  context,
  presentation = 'page',
}: LoginCreditOfferPanelProps) {
  const giftCredits = websiteConfig.credits?.registerGiftCredits?.amount ?? 0;

  if (!context || giftCredits <= 0) {
    return null;
  }

  const locale = getLocale() as LoginCreditOfferLocale;
  const usage =
    context.kind === 'signup'
      ? null
      : formatLoginCreditUsage({
          ...context,
          giftCredits,
          locale,
        });
  const videoTrial = context.kind === 'video' ? context.videoTrial : undefined;
  const visibleUsage = videoTrial ? null : usage;

  return (
    <div
      data-testid="login-credit-offer"
      className={cn(
        'text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]',
        presentation === 'modal'
          ? 'min-h-0 rounded-lg border border-amber-400/25 bg-amber-400/[0.08] px-3.5 py-3'
          : 'min-h-24 rounded-lg border border-[#5874a8]/55 bg-[#1c2945] px-5 py-4'
      )}
    >
      <div
        data-testid="login-credit-summary"
        className={cn(
          'flex items-center',
          presentation === 'modal' ? 'gap-3' : 'gap-4',
          presentation === 'modal' && videoTrial && 'mx-auto w-fit max-w-full'
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center bg-[#f3c94f] text-[#201a09] shadow-[0_8px_24px_rgba(243,201,79,0.22)]',
            presentation === 'modal'
              ? 'size-9 rounded-lg'
              : 'size-11 rounded-md'
          )}
        >
          <IconGift
            className={cn(presentation === 'modal' ? 'size-5' : 'size-6')}
            aria-hidden="true"
          />
        </span>
        <span className="min-w-0">
          <strong
            className={cn(
              'block font-semibold leading-tight',
              presentation === 'modal'
                ? 'text-xl text-foreground'
                : 'text-[1.05rem] text-white'
            )}
          >
            {giftCredits} {FREE_CREDIT_LABELS[locale]}
          </strong>
          {visibleUsage && (
            <span
              className={cn(
                'mt-1 block leading-5',
                presentation === 'modal'
                  ? 'text-xs text-muted-foreground'
                  : 'text-sm text-[#c8d4ea]'
              )}
            >
              {visibleUsage}
            </span>
          )}
        </span>
      </div>
      {videoTrial ? (
        <VideoCreditTrialGuidance
          {...videoTrial}
          cost={context.kind === 'video' ? context.cost : 0}
          presentation="login"
        />
      ) : null}
    </div>
  );
}
